import { createSelectorCreator, defaultMemoize } from 'reselect';
import isEqual from 'lodash.isequal';
import mapValues from 'lodash.mapvalues';
import keyBy from 'lodash.keyby';

import { AppState } from 'services/types';

// Utils
import formatters from 'forms/formatters';
import { Person, FormattedPerson } from './types';

// @ts-ignore
import orderBy from '../../utils/custom-orderBy';

const selectPeople = (state: AppState) => state.people;

const selectPersonId = (
    state: AppState,
    { personId }: { personId: Person['id'] }
) => personId;
const selectUserEmail = (
    state: AppState,
    { userEmail }: { userEmail: Person['email'] }
) => userEmail;

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

const selectFormattedPeople = createDeepEqualSelector(
    selectPeople,
    (peopleState): Record<Person['id'], FormattedPerson> =>
        mapValues(peopleState, (value) => ({
            ...value,
            fullName: `${value.firstName ? `${value.firstName} ` : ''}${
                value.lastName
            }`,
            mobileNumber: value.mobileNumber
                ? formatters.formatMobileNumberIn(value.mobileNumber)
                : ''
        }))
);

const selectPeopleArray = createDeepEqualSelector(
    selectFormattedPeople,
    (formattedPeopleState) => Object.values(formattedPeopleState)
);

const selectPeopleOrdered = createDeepEqualSelector(
    selectPeopleArray,
    (peopleArrayState) => orderBy(peopleArrayState, 'fullName')
);

const selectPerson = createDeepEqualSelector(
    [selectFormattedPeople, selectPersonId],
    (formattedPeopleState, personIdState) => formattedPeopleState[personIdState]
);

const selectPeopleKeyedById = createDeepEqualSelector(
    [selectFormattedPeople],
    (formattedPeopleState) => keyBy(formattedPeopleState, 'id')
);

const selectPeopleKeyedByUserId = createDeepEqualSelector(
    [selectPeopleArray],
    (peopleArrayState) =>
        keyBy(
            peopleArrayState.filter((people) => people.userId),
            'userId'
        )
);

const selectPeopleKeyedByDatacomId = createDeepEqualSelector(
    [selectPeopleArray],
    (peopleArrayState) =>
        keyBy(
            peopleArrayState.filter((people) => people.datacomId),
            'datacomId'
        )
);

const makeSelectPerson = () => selectPerson;

const makeSelectPersonValue = <K extends keyof FormattedPerson>(field: K) =>
    createDeepEqualSelector(
        selectPerson,
        (personState): FormattedPerson[K] => personState && personState[field]
    );

const selectUsersArray = createDeepEqualSelector(
    selectPeopleArray,
    (peopleArrayState) => peopleArrayState.filter((value) => value.email)
);

const selectUsers = createDeepEqualSelector(
    selectUsersArray,
    (usersArrayState) => keyBy(usersArrayState, 'email')
);

const selectUser = createDeepEqualSelector(
    [selectUsers, selectUserEmail],
    (usersState, userEmailState) => usersState[userEmailState]
);

const makeSelectUserValue = <K extends keyof FormattedPerson>(field: K) =>
    createDeepEqualSelector(
        selectUser,
        (userState): FormattedPerson[K] => userState && userState[field]
    );

export {
    selectPeople,
    selectFormattedPeople,
    selectPeopleArray,
    selectPersonId,
    selectPerson,
    makeSelectPerson,
    makeSelectPersonValue,
    selectUsersArray,
    selectUsers,
    selectUser,
    makeSelectUserValue,
    selectPeopleOrdered,
    selectPeopleKeyedById,
    selectPeopleKeyedByUserId,
    selectPeopleKeyedByDatacomId
};
